import React, { useState, useEffect, useMemo } from 'react';
import { useConfirm } from './ConfirmProvider';
import { formatCurrency } from '../utils/currency';
import { fetchClientStatementPdf, fetchAgedReceivables } from '../api';

/**
 * AgedReceivables Component – Tabbed Card Layout
 *
 * 5 aging buckets rendered as clickable cards:
 *   Not Yet Due | 0-30 | 31-60 | 61-90 | 90+ days
 *
 * Features:
 *   - Colour-coded bucket cards with counts & totals
 *   - Click a card to reveal the invoice table below
 *   - Persistent sticky filter bar (search, partner, sort)
 *   - Partner accountability section
 *   - Apply-trust one-click action per invoice
 */
const AgedReceivables = ({ authAxios, showToast, invoices, clients }) => {
  const confirm = useConfirm();

  // ── State ──────────────────────────────────────────────────────
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('daysOverdue');
  const [sortDir, setSortDir] = useState('desc');
  const [invoiceOverrides, setInvoiceOverrides] = useState({});
  const [loading, setLoading] = useState(true);

  // Bucket `key`s match the server's from reports/aged-receivables/. This
  // component used to bucket invoices itself in JavaScript, which is how four
  // independent and mutually disagreeing aging definitions grew up. The server
  // is now the single source of truth. [G-3][H3]
  // ── Bucket configuration ──────────────────────────────────────
  const BUCKETS = useMemo(() => [
    { key: 'current',  label: 'Not Yet Due', color: 'blue',   ring: 'ring-blue-400',   bg: 'bg-blue-50',   text: 'text-blue-700',   barBg: 'bg-blue-500' },
    { key: '30_days',     label: '1\u201330 Days',   color: 'green',  ring: 'ring-green-400',  bg: 'bg-green-50',  text: 'text-green-700',  barBg: 'bg-green-500' },
    { key: '60_days',    label: '31\u201360 Days',  color: 'yellow', ring: 'ring-yellow-400', bg: 'bg-yellow-50', text: 'text-yellow-700', barBg: 'bg-yellow-500' },
    { key: '90_days',    label: '61\u201390 Days',  color: 'orange', ring: 'ring-orange-400', bg: 'bg-orange-50', text: 'text-orange-700', barBg: 'bg-orange-500' },
    { key: 'over_90',      label: '90+ Days',    color: 'red',    ring: 'ring-red-400',    bg: 'bg-red-50',    text: 'text-red-700',    barBg: 'bg-red-500' },
  ], []);

  // ── Aging data, from the server ───────────────────────────────
  // Fetched rather than computed: reports/aged-receivables/ is now the one
  // definition of the buckets, so a debtor cannot show up in one bucket here
  // and a different one on their statement or in the annual audit pack.
  const [agingReport, setAgingReport] = useState(null);
  const [agingError, setAgingError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setAgingError('');
      try {
        const report = await fetchAgedReceivables(authAxios);
        if (!cancelled) setAgingReport(report);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load aged receivables', err);
          setAgingError(
            err?.response?.data?.detail || 'Could not load the aged receivables report.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // `invoices` is still a dependency so the report refreshes after an action
    // (e.g. applying trust) changes an invoice balance.
  }, [authAxios, invoices]);

  const { agingData, partnerSummary, totalOutstanding } = useMemo(() => {
    const buckets = {};
    BUCKETS.forEach(b => { buckets[b.key] = { count: 0, amount: 0, invoices: [] }; });
    if (!agingReport) {
      return { agingData: buckets, partnerSummary: [], totalOutstanding: 0 };
    }

    const partnerMap = {};
    BUCKETS.forEach(b => {
      const rows = agingReport.buckets?.[b.key] || [];
      rows.forEach(row => {
        // Local overrides (e.g. an invoice just paid from trust in this view)
        // still win, so the table stays responsive between refetches.
        const override = invoiceOverrides[row.invoice_id];
        const balance = override
          ? parseFloat(override.balance_due || 0)
          : parseFloat(row.balance || 0);
        if (balance <= 0) return;

        buckets[b.key].count++;
        buckets[b.key].amount += balance;
        buckets[b.key].invoices.push({
          ...row,
          id: row.invoice_id,
          balance_due: balance,
          daysOverdue: row.days_overdue,
        });

        const partner = row.responsible_partner_name || 'Unassigned';
        if (!partnerMap[partner]) partnerMap[partner] = { partner, count: 0, amount: 0 };
        partnerMap[partner].count++;
        partnerMap[partner].amount += balance;
      });
    });

    const total = Object.values(buckets).reduce((s, b) => s + b.amount, 0);
    return {
      agingData: buckets,
      partnerSummary: Object.values(partnerMap).sort((a, b) => b.amount - a.amount),
      totalOutstanding: total,
    };
  }, [agingReport, invoiceOverrides, BUCKETS]);

  // ── Client name helper ────────────────────────────────────────
  const getClientName = (clientId) => {
    const client = (clients || []).find(c => c.id === clientId);
    if (!client) return `Client ${clientId}`;
    return client.first_name ? `${client.first_name} ${client.last_name}` : client.organization_name;
  };

  // ── Apply trust funds ─────────────────────────────────────────
  const handleApplyTrust = async (invoice) => {
    try {
      const trust = parseFloat(invoice.available_trust_balance || 0);
      const balance = parseFloat(invoice.balance_due || 0);
      const amount = Math.min(trust, balance);
      if (!amount || amount <= 0) {
        showToast('No trust funds available to apply', 'error');
        return;
      }

      const ok = await confirm({
        title: 'Apply trust funds?',
        message: `Apply ${formatCurrency(amount)} from Trust to Invoice #${invoice.invoice_number}?`,
        confirmText: 'Apply',
        cancelText: 'Cancel',
        variant: 'warning',
      });
      if (!ok) return;

      const resp = await authAxios.post(`invoices/${invoice.id}/apply_trust/`, { amount });
      setInvoiceOverrides(prev => ({ ...prev, [resp.data.id]: resp.data }));
      showToast('Trust applied to invoice', 'success');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error || 'Failed to apply trust';
      showToast(detail, 'error');
    }
  };

  // ── Download client statement PDF ──────────────────────────────
  const handleDownloadStatement = async (clientId) => {
    try {
      const blob = await fetchClientStatementPdf(authAxios, clientId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement_client_${clientId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Failed to download statement', 'error');
    }
  };

  // ── Filtered & sorted invoices ─────────────────────────────────
  const filteredInvoices = useMemo(() => {
    // Gather source invoices: from selected bucket or all buckets
    let list;
    if (selectedBucket && agingData[selectedBucket]) {
      list = agingData[selectedBucket].invoices;
    } else {
      // All buckets combined
      list = Object.values(agingData).flatMap(b => b.invoices || []);
    }

    if (selectedPartner) {
      list = list.filter(inv => (inv.responsible_partner_name || 'Unassigned') === selectedPartner);
    }
    if (searchTerm.trim()) {
      const lc = searchTerm.toLowerCase();
      list = list.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(lc) ||
        getClientName(inv.client).toLowerCase().includes(lc)
      );
    }
    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      if (sortField === 'daysOverdue') return (a.daysOverdue - b.daysOverdue) * dir;
      if (sortField === 'balance') return (parseFloat(a.balance_due || 0) - parseFloat(b.balance_due || 0)) * dir;
      if (sortField === 'dueDate') return (new Date(a.due_date) - new Date(b.due_date)) * dir;
      return 0;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBucket, agingData, selectedPartner, searchTerm, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return '\u2195';
    return sortDir === 'asc' ? '\u2191' : '\u2193';
  };

  // Unique partners for filter dropdown
  const partnerOptions = useMemo(() => {
    const names = new Set();
    if (selectedBucket && agingData[selectedBucket]) {
      agingData[selectedBucket].invoices.forEach(inv => names.add(inv.responsible_partner_name || 'Unassigned'));
    } else {
      Object.values(agingData).forEach(b => {
        (b.invoices || []).forEach(inv => names.add(inv.responsible_partner_name || 'Unassigned'));
      });
    }
    return [...names].sort();
  }, [selectedBucket, agingData]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-1">Aged Receivables</h2>
        <p className="text-gray-600">
          Outstanding invoices by aging bucket &bull; Total: <span className="font-semibold text-blue-600">{formatCurrency(totalOutstanding)}</span>
        </p>
      </div>

      {agingError && (
        <div role="alert" className="mb-4 border border-red-300 bg-red-50 text-red-800 rounded-lg px-4 py-3 text-sm">
          {agingError}
        </div>
      )}

      {/* The report states plainly whether the invoice sub-ledger agrees with
          the GL Accounts Receivable control account, rather than leaving the
          two to drift apart unnoticed. */}
      {agingReport?.gl_reconciliation && !agingReport.gl_reconciliation.reconciled && (
        <div role="alert" className="mb-4 border border-amber-300 bg-amber-50 text-amber-900 rounded-lg px-4 py-3 text-sm">
          <span className="font-semibold">Does not reconcile to the general ledger.</span>{' '}
          Outstanding invoices total {formatCurrency(agingReport.gl_reconciliation.sub_ledger_balance)},
          but GL account {agingReport.gl_reconciliation.ar_control_account || 'AR'} shows{' '}
          {formatCurrency(agingReport.gl_reconciliation.gl_balance)} — a variance of{' '}
          {formatCurrency(agingReport.gl_reconciliation.variance)}.
        </div>
      )}

      {/* Bucket Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {BUCKETS.map(bucket => {
          const data = agingData[bucket.key];
          const isActive = selectedBucket === bucket.key;
          return (
            <button
              key={bucket.key}
              type="button"
              onClick={() => setSelectedBucket(isActive ? null : bucket.key)}
              className={`p-4 rounded-lg shadow-sm border-2 transition-all text-left
                ${isActive ? `${bucket.ring} ring-2 ${bucket.bg} border-transparent` : 'bg-white border-gray-200 hover:shadow-md'}
              `}
            >
              <div className={`text-xs font-semibold uppercase tracking-wide ${bucket.text}`}>{bucket.label}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{data.count}</div>
              <div className="text-sm text-gray-600 mt-1">{formatCurrency(data.amount)}</div>
              {/* Mini bar */}
              <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${bucket.barBg} rounded-full transition-all`}
                  style={{ width: `${totalOutstanding > 0 ? (data.amount / totalOutstanding) * 100 : 0}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Partner Accountability */}
      {totalOutstanding > 0 && partnerSummary.length > 0 && (
        <div className="bg-white p-5 rounded-lg shadow-sm mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Outstanding by Responsible Partner</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {partnerSummary.map(p => {
              const pct = totalOutstanding > 0 ? (p.amount / totalOutstanding) * 100 : 0;
              const isActive = selectedPartner === p.partner;
              return (
                <button
                  key={p.partner}
                  type="button"
                  onClick={() => setSelectedPartner(isActive ? '' : p.partner)}
                  className={`flex items-center justify-between p-3 rounded-md border transition-all
                    ${isActive ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-300' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <div>
                    <div className={`text-sm font-medium ${isActive ? 'text-blue-700' : 'text-gray-800'}`}>{p.partner}</div>
                    <div className="text-xs text-gray-500">{p.count} invoice{p.count !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">{formatCurrency(p.amount)}</div>
                    <div className="text-xs text-gray-500">{pct.toFixed(1)}%</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sticky Filter Bar */}
      {totalOutstanding > 0 && (
        <div className="sticky top-0 z-10 bg-white rounded-lg shadow-sm mb-4 p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 border border-gray-200">
          <div className="flex items-center gap-2 text-sm">
            <span className={`font-semibold ${selectedBucket ? (BUCKETS.find(b => b.key === selectedBucket)?.text || 'text-gray-700') : 'text-gray-700'}`}>
              {selectedBucket ? BUCKETS.find(b => b.key === selectedBucket)?.label : 'All Buckets'}
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">{filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <input
              type="text"
              placeholder="Search invoice # or client\u2026"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={selectedPartner}
              onChange={e => setSelectedPartner(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
            >
              <option value="">All Partners</option>
              {partnerOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <button
            type="button"
            onClick={() => { setSelectedBucket(null); setSelectedPartner(''); setSearchTerm(''); }}
            className="text-sm text-gray-500 hover:text-gray-800"
          >
            Clear All
          </button>
        </div>
      )}

      {/* Invoice Table */}
      {totalOutstanding > 0 && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {filteredInvoices.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No invoices match the current filters</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none" onClick={() => toggleSort('dueDate')}>
                      Due Date {sortIcon('dueDate')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none" onClick={() => toggleSort('daysOverdue')}>
                      Days Overdue {sortIcon('daysOverdue')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer select-none" onClick={() => toggleSort('balance')}>
                      Balance Due {sortIcon('balance')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Trust</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fees</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Disb.</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredInvoices.map(invoice => {
                    const trust = parseFloat(invoice.available_trust_balance || 0);
                    const balance = parseFloat(invoice.balance_due || 0);
                    const canApplyAny = trust > 0 && balance > 0;
                    const canOffset = trust >= balance && balance > 0;
                    const bucketCfg = selectedBucket
                      ? BUCKETS.find(b => b.key === selectedBucket)
                      : BUCKETS.find(b => {
                          if (invoice.daysOverdue === 0 && new Date(invoice.due_date) > new Date()) return b.key === 'current';
                          if (invoice.daysOverdue <= 30) return b.key === '30_days';
                          if (invoice.daysOverdue <= 60) return b.key === '60_days';
                          if (invoice.daysOverdue <= 90) return b.key === '90_days';
                          return b.key === 'over_90';
                        });
                    return (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">#{invoice.invoice_number}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">{getClientName(invoice.client)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">{invoice.responsible_partner_name || 'Unassigned'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">{invoice.due_date}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`font-medium ${bucketCfg?.text || 'text-gray-700'}`}>
                            {invoice.daysOverdue} days
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right font-semibold text-gray-900">{formatCurrency(balance)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span className={`font-semibold ${canOffset ? 'text-green-700' : 'text-gray-700'}`}>{formatCurrency(trust)}</span>
                          {canOffset && <div className="text-xs text-green-600">Can offset</div>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-gray-700">{formatCurrency(parseFloat(invoice.professional_fees_total || 0))}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-gray-700">{formatCurrency(parseFloat(invoice.disbursements_total || 0))}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">{invoice.status}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center space-x-1">
                          <button
                            type="button"
                            onClick={() => handleApplyTrust(invoice)}
                            disabled={!canApplyAny}
                            className={`px-3 py-1 rounded-md text-xs font-medium shadow-sm ${canApplyAny ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                            title={canApplyAny ? 'Apply trust funds' : 'No trust funds available'}
                          >
                            Apply Trust
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadStatement(invoice.client)}
                            className="px-3 py-1 rounded-md text-xs font-medium shadow-sm bg-blue-600 text-white hover:bg-blue-700"
                            title="Download client statement PDF"
                          >
                            Statement
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {totalOutstanding === 0 && (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <svg className="mx-auto h-12 w-12 text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">All Caught Up!</h3>
          <p className="text-gray-600">No outstanding invoices at this time</p>
        </div>
      )}
    </div>
  );
};

export default AgedReceivables;
